import { useCallback, useEffect, useMemo, useState, memo, useRef } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { ClipboardCheck, Info, AlertTriangle, ChevronRight, ChevronLeft } from "lucide-react";
import toast, { Toaster } from "react-hot-toast";
import { getCourseExams, submitExam } from "../services/exams";
import { motion } from "framer-motion";



import renderMathInElement from "katex/dist/contrib/auto-render";

const sanitizeHtml = (html = "") => (
  html
    .replace(/<script[\s\S]*?>[\s\S]*?<\/script>/gi, "")
    .replace(/\son\w+="[^"]*"/gi, "")
    .replace(/\son\w+='[^']*'/gi, "")
    .replace(/<video(?![^>]*\bcontrols\b)/gi, "<video controls")
);

const RichContent = memo(function RichContent({ html, className = "" }) {
  const [modalMedia, setModalMedia] = useState(null);
  const contentRef = useRef(null);

  // Handle math rendering and video click-to-play
  useEffect(() => {
    if (contentRef.current) {
      try {
        renderMathInElement(contentRef.current, {
          delimiters: [
            { left: "$$", right: "$$", display: true },
            { left: "\\(", right: "\\)", display: false },
            { left: "$", right: "$", display: false },
            { left: "\\[", right: "\\]", display: true }
          ],
          throwOnError: false
        });
      } catch (e) { console.error("Math render failed:", e); }

      // Attach click-to-play for videos using an overlay
      const videos = contentRef.current.querySelectorAll("video");
      const cleanups = [];

      videos.forEach(v => {
        // Prevent wrapping multiple times if html re-renders but video is same
        if (v.parentElement && v.parentElement.classList.contains("video-wrapper")) return;

        const wrapper = document.createElement("div");
        wrapper.className = "video-wrapper relative inline-block w-full max-w-full";
        v.parentNode.insertBefore(wrapper, v);
        wrapper.appendChild(v);

        const overlay = document.createElement("div");
        overlay.className = "absolute top-0 left-0 right-0 cursor-zoom-in z-20";
        // Leave bottom 55px for native controls so they can still play inline if they want
        overlay.style.bottom = "55px";
        wrapper.appendChild(overlay);

        const handler = (e) => {
          e.preventDefault();
          e.stopPropagation();
          // Pause the inline video when opening modal so audio doesn't overlap
          v.pause(); 
          setModalMedia({
            type: "VIDEO",
            src: v.src || v.querySelector("source")?.src
          });
        };

        overlay.addEventListener("click", handler);
        cleanups.push(() => {
          overlay.removeEventListener("click", handler);
        });
      });

      return () => {
        cleanups.forEach(cleanup => cleanup());
      };
    }
  }, [html]);

  const handleClick = (e) => {
    if (e.target.tagName === "IMG") {
      setModalMedia({
        type: e.target.tagName,
        src: e.target.src
      });
    }
  };

  return (
    <>
      <div
        ref={contentRef}
        onClick={handleClick}
        className={`prose prose-sm max-w-none [&_img]:max-w-full [&_img]:h-auto [&_img]:object-contain [&_img]:cursor-zoom-in [&_img]:rounded-lg [&_video]:w-full [&_video]:max-w-full [&_video]:h-auto [&_video]:object-contain [&_video]:rounded-lg [&_video]:pointer-events-auto [&_video]:relative [&_video]:z-10 ${className}`}
        dangerouslySetInnerHTML={{ __html: sanitizeHtml(html) }}
      />

      {modalMedia && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4 backdrop-blur-sm"
          onClick={() => setModalMedia(null)}
        >
          <div
            className="relative max-h-full max-w-full flex items-center justify-center"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              className="absolute -top-12 right-0 flex items-center gap-2 text-white hover:text-slate-300 font-bold bg-black/50 px-4 py-2 rounded-full transition-colors"
              onClick={() => setModalMedia(null)}
            >
              Close
            </button>
            {modalMedia.type === "IMG" ? (
              <img
                src={modalMedia.src}
                alt="Expanded"
                className="max-h-[85vh] max-w-[90vw] object-contain rounded-lg shadow-2xl bg-white"
              />
            ) : (
              <video
                src={modalMedia.src}
                controls
                autoPlay
                className="max-h-[85vh] max-w-[90vw] rounded-lg shadow-2xl bg-black"
              />
            )}
          </div>
        </div>
      )}
    </>
  );
});

const UI_LABELS = {
  English: {
    instructions: "Group wise Instructions",
    viewIn: "View in:",
    groupsIntro: (count) => `There are ${count} Groups in this examination as follows :-`,
    groupItem: (i, group, sectionCount, list) => `Group ${i + 1} - ${group} : This group contains ${sectionCount} section${sectionCount !== 1 ? "s" : ""}${sectionCount > 0 ? ` - ${list}` : ""}. All the questions in ${sectionCount === 1 ? "this section" : "these sections"} are mandatory for you to attempt. You can view your responses even after submitting the group.`,
    generalInst: "General Instructions :",
    instItems: [
      "Some of the questions contain Audio / Video contents. Please ensure that your system supports to hear the audio and play the video clearly.",
      "For the questions that require upload of files, please ensure that the file size does not exceed the specified limit.",
      "You can view these groups even after submitting the Group.",
      "You can edit the responses in this group as well after submitting the Group.",
      "For the questions that require Audio / Video response, please ensure that you have the microphone/webcam connected to the system and the browser is allowed to access the microphone/webcam."
    ],
    note: "Note : You can view these instructions any time during the assessment by clicking the link 'Instructions' at the top right of the candidate console.",
    understand: "I have read and understood the instructions.",
    back: "Back",
    resume: "Resume Exam",
    start: "Start Exam",
    pause: "Pause Exam",
    groupInstBtn: "Group Instructions",
    instBtn: "Instructions",
    paperBtn: "Question Paper",
    timeLeft: "Time Left :",
    sections: "Sections",
    qType: "Question Type:",
    marks: "Marks for correct answer:",
    negMarks: "Negative Marks:",
    qNo: "Question No.",
    mandatoryAlert: "Note: This question is mandatory you have to answer it.",
    notEditAlert: "Note: You cannot edit the response for this question upon navigating. Please take care not to move to previous/next question without attempting this Question.",
    notRevisitAlert: "Note: You cannot revisit this question upon navigating. Please take care not to move to previous/next question without attempting this Question.",
    markReview: "Mark for Review & Next",
    clear: "Clear Response",
    prev: "Previous",
    saveNext: "Save & Next",
    submit: "Submit",
    student: "Student",
    chooseQ: "Choose a Question",
    notVis: "Not Visited",
    notAns: "Not Answered",
    ans: "Answered",
    markRev: "Marked for Review",
    ansMark: "Answered & Marked for Review",
    calculator: "Calculator"
  },
  Hindi: {
    instructions: "समूहवार निर्देश (Instructions)",
    viewIn: "भाषा चुनें (View in):",
    groupsIntro: (count) => `इस परीक्षा में कुल ${count} समूह निम्नलिखित हैं :-`,
    groupItem: (i, group, sectionCount, list) => `समूह ${i + 1} - ${group} : इस समूह में ${sectionCount} अनुभाग ${sectionCount > 0 ? ` - ${list}` : ""} शामिल हैं। ${sectionCount === 1 ? "इस अनुभाग" : "इन अनुभागों"} के सभी प्रश्न आपके प्रयास करने के लिए अनिवार्य हैं। आप समूह जमा करने के बाद भी अपनी प्रतिक्रियाएँ देख सकते हैं।`,
    generalInst: "सामान्य निर्देश :",
    instItems: [
      "कुछ प्रश्नों में ऑडियो / वीडियो सामग्री शामिल हैं। कृपया सुनिश्चित करें कि आपका सिस्टम ऑडियो सुनने और वीडियो को स्पष्ट रूप से चलाने का समर्थन करता है।",
      "जिन प्रश्नों के लिए फाइल अपलोड करने की आवश्यकता होती है, कृपया सुनिश्चित करें कि फाइल का आकार निर्दिष्ट सीमा से अधिक न हो।",
      "आप इन समूहों को सबमिट करने के बाद भी देख सकते हैं।",
      "आप समूह को सबमिट करने के बाद इस समूह में प्रतिक्रियाओं को संपादित भी कर सकते हैं।",
      "जिन प्रश्नों के लिए ऑडियो / वीडियो प्रतिक्रिया की आवश्यकता होती है, कृपया सुनिश्चित करें कि आपने माइक्रोफ़ोन/वेबकैम को सिस्टम से कनेक्ट किया है और ब्राउज़र को माइक्रोफ़ोन/वेबकैम तक पहुंचने की अनुमति दी है।"
    ],
    note: "नोट: आप उम्मीदवार कंसोल के शीर्ष दाईं ओर स्थित 'निर्देश' लिंक पर क्लिक करके मूल्यांकन के दौरान किसी भी समय इन निर्देशों को देख सकते हैं।",
    understand: "मैंने निर्देशों को ध्यानपूर्वक पढ़ और समझ लिया है।",
    back: "पीछे (Back)",
    resume: "परीक्षा जारी रखें (Resume)",
    start: "परीक्षा शुरू करें (Start)",
    pause: "परीक्षा रोकें (Pause)",
    groupInstBtn: "समूह निर्देश",
    instBtn: "निर्देश",
    paperBtn: "प्रश्न पत्र",
    timeLeft: "समय शेष :",
    sections: "अनुभाग (Sections)",
    qType: "प्रश्न का प्रकार:",
    marks: "सही उत्तर के लिए अंक:",
    negMarks: "ऋणात्मक अंक:",
    qNo: "प्रश्न संख्या",
    mandatoryAlert: "नोट: यह प्रश्न अनिवार्य है आपको इसका उत्तर देना होगा।",
    notEditAlert: "नोट: आप आगे बढ़ने पर इस प्रश्न के उत्तर को संपादित नहीं कर सकते। कृपया उत्तर दिए बिना अगले/पिछले प्रश्न पर न जाएं।",
    notRevisitAlert: "नोट: आप आगे बढ़ने पर इस प्रश्न को दोबारा नहीं देख सकते। कृपया उत्तर दिए बिना अगले/पिछले प्रश्न पर न जाएं।",
    markReview: "मार्क फॉर रिव्यू और नेक्स्ट",
    clear: "प्रतिक्रिया साफ़ करें",
    prev: "पिछला (Previous)",
    saveNext: "सहेजें और अगला (Save & Next)",
    submit: "जमा करें (Submit)",
    student: "छात्र",
    chooseQ: "प्रश्न चुनें",
    notVis: "विजिट नहीं किया",
    notAns: "उत्तर नहीं दिया",
    ans: "उत्तर दिया",
    markRev: "समीक्षा के लिए चिह्नित",
    ansMark: "उत्तर दिया और समीक्षा के लिए चिह्नित",
    calculator: "कैलकुलेटर"
  }
};

const translateTextREST = async (text, target = "hi") => {
  if (!text || !text.trim()) return text;
  try {
    const url = `https://translate.googleapis.com/translate_a/single?client=gtx&sl=en&tl=${target}&dt=t&q=${encodeURIComponent(text)}`;
    const res = await fetch(url);
    if (res.ok) {
      const data = await res.json();
      return data[0].map(x => x[0]).join("");
    }
    return text;
  } catch (e) {
    console.error("Local translate bridge failed:", e);
    return text;
  }
};

export default function OnlineExam() {
  const { courseId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [exams, setExams] = useState([]);
  const [selectedExamId, setSelectedExamId] = useState("");
  const [started, setStarted] = useState(false);
  const [accepted, setAccepted] = useState(false);
  const [questionIndex, setQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [visited, setVisited] = useState({});
  const [reviewMarks, setReviewMarks] = useState({});
  const [result, setResult] = useState(null);
  const [secondsLeft, setSecondsLeft] = useState(0);
  const [activeGroup, setActiveGroup] = useState("");
  const [activeSection, setActiveSection] = useState("");
  const [isPaused, setIsPaused] = useState(false);
  const [activePopup, setActivePopup] = useState(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(() => typeof window !== "undefined" ? window.innerWidth >= 1024 : true);
  const [locked, setLocked] = useState({});
  const [isCalculatorOpen, setIsCalculatorOpen] = useState(false);
  const [calcInput, setCalcInput] = useState("");
  const [language, setLanguage] = useState("English");

  const [translationCache, setTranslationCache] = useState({});
  const [isTranslating, setIsTranslating] = useState(false);

  useEffect(() => {
    getCourseExams(courseId).then((res) => {
      const data = res.data || [];
      const requestedExamId = new URLSearchParams(location.search).get("examId");
      setExams(data);
      if (data.length > 0) {
        const requestedExam = data.find((item) => String(item.id) === requestedExamId);
        setSelectedExamId(String(requestedExam?.id || data[0].id));
      }
    }).catch(() => {
      toast.error("Failed to load exams");
    });
  }, [courseId, location.search]);

  const exam = useMemo(() => exams.find((item) => String(item.id) === selectedExamId), [exams, selectedExamId]);
  const groupedQuestions = useMemo(() => {
    const groups = {};
    (exam?.questions || []).forEach((question) => {
      const group = question.group_name || "General Knowledge";
      const section = question.section_name || "Section 1";
      if (!groups[group]) groups[group] = {};
      if (!groups[group][section]) groups[group][section] = [];
      groups[group][section].push(question);
    });
    return groups;
  }, [exam]);
  const groupNames = useMemo(() => Object.keys(groupedQuestions), [groupedQuestions]);
  const sectionNames = useMemo(() => Object.keys(groupedQuestions[activeGroup] || {}), [groupedQuestions, activeGroup]);
  const visibleQuestions = useMemo(() => groupedQuestions[activeGroup]?.[activeSection] || [], [groupedQuestions, activeGroup, activeSection]);

  useEffect(() => {
    if (!exam || groupNames.length === 0) return;
    const nextGroup = groupNames.includes(activeGroup) ? activeGroup : groupNames[0];
    const sections = Object.keys(groupedQuestions[nextGroup] || {});
    const nextSection = sections.includes(activeSection) ? activeSection : sections[0];
    setActiveGroup(nextGroup);
    setActiveSection(nextSection);
    setQuestionIndex(0);
  }, [exam, groupNames, groupedQuestions, activeGroup, activeSection]);

  useEffect(() => {
    if (!started || result || secondsLeft <= 0 || isPaused) return;
    const id = setInterval(() => setSecondsLeft((value) => Math.max(0, value - 1)), 1000);
    return () => clearInterval(id);
  }, [started, result, secondsLeft, isPaused]);

  const handleSubmit = useCallback(async () => {
    if (!exam || result) return;
    const unansweredMandatory = exam.questions.find(q => q.is_mandatory && !answers[q.id]);
    if (unansweredMandatory) {
      toast.error("There are mandatory questions you have to answer.");
      return;
    }
    if (!window.confirm("Are you sure you want to submit the exam?")) return;
    try {
      const res = await submitExam(exam.id, answers);
      localStorage.removeItem(`exam_state_${exam.id}`);
      toast.success("Exam submitted");
      navigate(`/exam-result/${res.data.id}`, { replace: true });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to submit exam");
    }
  }, [answers, exam, result, navigate]);

  useEffect(() => {
    if (started && secondsLeft === 0 && exam && !result) {
      handleSubmit();
    }
  }, [secondsLeft, started, exam, result, handleSubmit]);

  useEffect(() => {
    if (started && exam && !result) {
      const stateToSave = {
        secondsLeft,
        answers,
        visited,
        reviewMarks,
        locked,
        questionIndex,
        activeGroup,
        activeSection
      };
      localStorage.setItem(`exam_state_${exam.id}`, JSON.stringify(stateToSave));
    }
  }, [secondsLeft, answers, visited, reviewMarks, locked, questionIndex, activeGroup, activeSection, started, exam, result]);

  const currentQuestion = visibleQuestions[questionIndex];
  const formattedTime = `${String(Math.floor(secondsLeft / 60)).padStart(2, "0")}:${String(secondsLeft % 60).padStart(2, "0")}`;

  useEffect(() => {
    if (language === "Hindi" && currentQuestion) {
      const qId = currentQuestion.id;
      if (translationCache[qId] || isTranslating) return;

      const runTranslate = async () => {
        setIsTranslating(true);
        try {
          const promptTrans = await translateTextREST(currentQuestion.prompt);
          const linkedTrans = currentQuestion.linked_prompt ? await translateTextREST(currentQuestion.linked_prompt) : null;

          const optATrans = currentQuestion.option_a ? await translateTextREST(currentQuestion.option_a) : null;
          const optBTrans = currentQuestion.option_b ? await translateTextREST(currentQuestion.option_b) : null;
          const optCTrans = currentQuestion.option_c ? await translateTextREST(currentQuestion.option_c) : null;
          const optDTrans = currentQuestion.option_d ? await translateTextREST(currentQuestion.option_d) : null;

          let optsArrayTrans = [];
          if (currentQuestion.options && currentQuestion.options.length > 0) {
            optsArrayTrans = await Promise.all(currentQuestion.options.map(async (o) => {
              const tText = await translateTextREST(o.text);
              return { ...o, text: tText };
            }));
          }

          setTranslationCache(prev => ({
            ...prev,
            [qId]: {
              prompt: promptTrans,
              linked_prompt: linkedTrans,
              option_a: optATrans,
              option_b: optBTrans,
              option_c: optCTrans,
              option_d: optDTrans,
              options: optsArrayTrans
            }
          }));
        } catch (e) {
          console.error("Problem translating question:", e);
        } finally {
          setIsTranslating(false);
        }
      };
      runTranslate();
    }
  }, [language, currentQuestion, translationCache, isTranslating]);

  const renderQText = (prop) => {
    if (language === "Hindi" && currentQuestion) {
      const cached = translationCache[currentQuestion.id];
      if (cached && cached[prop]) return cached[prop];
    }
    return currentQuestion?.[prop] || "";
  };

  const checkNavigationAllowed = (targetQuestion) => {
    if (currentQuestion?.is_mandatory && !answers[currentQuestion.id]) {
      toast.error("This question is mandatory you have to answer it");
      return false;
    }
    if (targetQuestion?.cannot_revisit && locked[targetQuestion.id]) {
      toast.error("You cannot revisit this question.");
      return false;
    }
    return true;
  };

  const markQuestionLocked = () => {
    if (currentQuestion) {
      setLocked((prev) => ({ ...prev, [currentQuestion.id]: true }));
    }
  };

  const setQuestion = (index) => {
    const targetQuestion = visibleQuestions[index];
    if (index !== questionIndex && !checkNavigationAllowed(targetQuestion)) return;
    if (index !== questionIndex) markQuestionLocked();

    setQuestionIndex(index);
    if (targetQuestion) {
      setVisited((prev) => ({ ...prev, [targetQuestion.id]: true }));
    }
    setIsCalculatorOpen(false);
  };

  const setAnswer = (questionId, option) => {
    setAnswers((prev) => ({ ...prev, [questionId]: option }));
    setVisited((prev) => ({ ...prev, [questionId]: true }));
  };

  const clearAnswer = () => {
    if (!currentQuestion) return;
    setAnswers((prev) => {
      const next = { ...prev };
      delete next[currentQuestion.id];
      return next;
    });
  };

  const jumpToSection = (group, section) => {
    if (activeGroup === group && activeSection === section) return;

    const targetList = groupedQuestions[group]?.[section] || [];
    let targetIdx = 0;
    while (targetIdx < targetList.length) {
      const q = targetList[targetIdx];
      if (!(q.cannot_revisit && locked[q.id])) break;
      targetIdx++;
    }

    if (targetIdx >= targetList.length) {
      toast.error("All questions in this section are locked and cannot be revisited.");
      return;
    }

    const nextQ = targetList[targetIdx];
    if (!checkNavigationAllowed(nextQ)) return;

    markQuestionLocked();
    setActiveGroup(group);
    setActiveSection(section);
    setQuestionIndex(targetIdx);
    if (nextQ) setVisited(prev => ({ ...prev, [nextQ.id]: true }));
  };

  const moveToNext = () => {
    if (!checkNavigationAllowed(null)) return;

    let target = questionIndex + 1;
    while (target < visibleQuestions.length) {
      const q = visibleQuestions[target];
      if (!(q.cannot_revisit && locked[q.id])) {
        break;
      }
      target++;
    }

    if (target < visibleQuestions.length) {
      markQuestionLocked();
      setQuestionIndex(target);
      const nextQ = visibleQuestions[target];
      if (nextQ) setVisited(prev => ({ ...prev, [nextQ.id]: true }));
    } else {
      const currentSectionIndex = sectionNames.indexOf(activeSection);
      if (currentSectionIndex < sectionNames.length - 1) {
        jumpToSection(activeGroup, sectionNames[currentSectionIndex + 1]);
      } else {
        const currentGroupIndex = groupNames.indexOf(activeGroup);
        if (currentGroupIndex < groupNames.length - 1) {
          const nextGrp = groupNames[currentGroupIndex + 1];
          const nextSec = Object.keys(groupedQuestions[nextGrp] || {})[0];
          jumpToSection(nextGrp, nextSec);
        } else {
          toast.success("You have reached the end of the exam. Please submit if you are done.");
        }
      }
    }
  };

  const handlePrevious = () => {
    let target = questionIndex - 1;
    while (target >= 0) {
      const q = visibleQuestions[target];
      if (!(q.cannot_revisit && locked[q.id])) {
        break;
      }
      target--;
    }
    if (target >= 0) {
      setQuestion(target);
    } else {
      toast.error("No previous unlocked questions available in this section.");
    }
  };

  const saveAndNext = () => {
    moveToNext();
  };

  const markForReviewAndNext = () => {
    if (currentQuestion) setReviewMarks((prev) => ({ ...prev, [currentQuestion.id]: true }));
    moveToNext();
  };

  const getStatus = useCallback((question) => {
    const isVisited = Boolean(visited[question.id]);
    const isAnswered = Boolean(answers[question.id]);
    const isMarked = Boolean(reviewMarks[question.id]);

    if (isAnswered && isMarked) return "answeredMarked";
    if (isMarked) return "marked";
    if (isAnswered) return "answered";
    if (isVisited) return "notAnswered";
    return "notVisited";
  }, [visited, answers, reviewMarks]);

  const statusCounts = useMemo(() => {
    const counts = {
      notVisited: 0,
      notAnswered: 0,
      answered: 0,
      marked: 0,
      answeredMarked: 0,
    };
    if (!exam) return counts;
    exam.questions.forEach((q) => {
      counts[getStatus(q)]++;
    });
    return counts;
  }, [exam, getStatus]);

  const getGroupStatusCounts = useCallback((groupName) => {
    const counts = {
      notVisited: 0,
      notAnswered: 0,
      answered: 0,
      marked: 0,
      answeredMarked: 0,
    };
    if (!exam) return counts;
    exam.questions.forEach((q) => {
      if ((q.group_name || "General Knowledge") === groupName) {
        counts[getStatus(q)]++;
      }
    });
    return counts;
  }, [exam, getStatus]);

  const statusClass = (status) => {
    if (status === "answered") return "bg-[#5cb85c] text-white [clip-path:polygon(50%_0,100%_25%,100%_75%,50%_100%,0_75%,0_25%)]";
    if (status === "notAnswered") return "bg-[#d9534f] text-white [clip-path:polygon(0_0,100%_0,100%_75%,50%_100%,0_75%)]";
    if (status === "marked" || status === "answeredMarked") return "rounded-full bg-[#825a9c] text-white";
    return "bg-[#e2e2e2] text-slate-900 border border-[#ccc] rounded-sm";
  };

  const handleStart = () => {
    if (!exam) return toast.error("No exam selected");
    if (!accepted) return toast.error("Please accept the instructions");
    if (isPaused) {
      setIsPaused(false);
      return;
    }

    setResult(null);
    setIsPaused(false);

    const savedState = localStorage.getItem(`exam_state_${exam.id}`);
    if (savedState) {
      try {
        const parsed = JSON.parse(savedState);
        setAnswers(parsed.answers || {});
        setVisited(parsed.visited || {});
        setReviewMarks(parsed.reviewMarks || {});
        setLocked(parsed.locked || {});
        setQuestionIndex(parsed.questionIndex || 0);
        setActiveGroup(parsed.activeGroup || groupNames[0]);
        setActiveSection(parsed.activeSection || Object.keys(groupedQuestions[parsed.activeGroup || groupNames[0]] || {})[0]);
        setSecondsLeft(parsed.secondsLeft ?? (exam.duration_minutes * 60));
      } catch (e) {
        setAnswers({});
        setVisited({});
        setReviewMarks({});
        setQuestionIndex(0);
        setSecondsLeft(exam.duration_minutes * 60);
        setVisited((prev) => ({ ...prev, [visibleQuestions[0]?.id]: true }));
      }
    } else {
      setAnswers({});
      setVisited({});
      setReviewMarks({});
      setLocked({});
      setQuestionIndex(0);
      setSecondsLeft(exam.duration_minutes * 60);
      setVisited((prev) => ({ ...prev, [visibleQuestions[0]?.id]: true }));
    }

    setStarted(true);
  };

  if (exams.length === 0) {
    return (
      <div className="min-h-screen bg-slate-100 p-8 text-center">
        <Toaster position="top-center" />
        <ClipboardCheck className="mx-auto mb-4 text-slate-300" size={56} />
        <p className="font-bold text-slate-600">No exams published for this course yet.</p>
        <button onClick={() => navigate(-1)} className="mt-4 text-blue-600 font-bold">Go Back</button>
      </div>
    );
  }

  if (!started || isPaused) {
    return (
      <div
        className="min-h-screen bg-slate-100 p-4 lg:p-8 select-none"
        onCopy={(e) => e.preventDefault()}
        onCut={(e) => e.preventDefault()}
        onPaste={(e) => e.preventDefault()}
        onContextMenu={(e) => e.preventDefault()}
      >
        <Toaster position="top-center" />
        <div className="mx-auto max-w-5xl bg-white border border-slate-300 shadow-sm">
          <div className="bg-neutral-800 px-4 py-2 text-sm font-bold text-yellow-300">{exam?.title || "Mock Assessment"}</div>
          <div className="p-6 space-y-6 text-sm leading-6">
            <div className="flex flex-col md:flex-row gap-3 md:items-center md:justify-between">
              <h1 className="text-2xl font-black text-slate-900">{UI_LABELS[language].instructions}</h1>
              <div className="flex items-center gap-2 text-sm font-bold border border-slate-300 bg-slate-50 px-3 py-2.5 rounded-lg">
                <span>{UI_LABELS[language].viewIn}</span>
                <select
                  value={language}
                  onChange={(e) => setLanguage(e.target.value)}
                  className="bg-transparent text-slate-900 font-black border-none outline-none cursor-pointer focus:ring-0"
                >
                  <option value="English">English</option>
                  <option value="Hindi">Hindi</option>
                </select>
              </div>
            </div>

            <section>
              <p className="font-bold mb-2">{UI_LABELS[language].groupsIntro(groupNames.length)}</p>
              <ol className="list-decimal pl-6">
                {groupNames.map((group) => <li key={group}>{group}</li>)}
              </ol>
            </section>

            <section className="space-y-3">
              {groupNames.map((group, i) => {
                const sectionsInGroup = Object.keys(groupedQuestions[group] || {});
                const sectionCount = sectionsInGroup.length;
                const sectionListText = sectionsInGroup.join(", ");
                return (
                  <p key={group}>
                    {UI_LABELS[language].groupItem(i, group, sectionCount, sectionListText)}
                  </p>
                );
              })}
            </section>

            <section>
              <h2 className="font-black text-lg mb-2">{UI_LABELS[language].generalInst}</h2>
              <ol className="list-decimal pl-6 space-y-1">
                {UI_LABELS[language].instItems.map((item, index) => <li key={index}>{item}</li>)}
              </ol>
            </section>

            <div className="border-t pt-4">
              <p>{UI_LABELS[language].note}</p>
              <label className="mt-4 flex items-center gap-2 font-bold">
                <input type="checkbox" checked={accepted} onChange={(e) => setAccepted(e.target.checked)} />
                {UI_LABELS[language].understand}
              </label>
            </div>

            <div className="flex justify-end gap-3">
              <button onClick={() => navigate(-1)} className="border px-6 py-2">{UI_LABELS[language].back}</button>
              {isPaused || (exam && localStorage.getItem(`exam_state_${exam.id}`)) ? (
                <button onClick={isPaused ? () => setIsPaused(false) : handleStart} className="bg-sky-700 px-8 py-2 font-bold text-white">{UI_LABELS[language].resume}</button>
              ) : (
                <button onClick={handleStart} className="bg-sky-700 px-8 py-2 font-bold text-white">{UI_LABELS[language].start}</button>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="h-screen bg-slate-100 p-2 overflow-hidden select-none"
      onCopy={(e) => e.preventDefault()}
      onCut={(e) => e.preventDefault()}
      onPaste={(e) => e.preventDefault()}
      onContextMenu={(e) => e.preventDefault()}
    >
      <Toaster position="top-center" />
      <div className="flex flex-col h-full border border-slate-300 bg-white text-slate-950 shadow-sm overflow-hidden">
        <div className="flex flex-col md:flex-row md:items-center justify-between bg-neutral-800 px-3 py-2 text-white gap-2">
          <div className="text-sm font-bold text-yellow-300 truncate">{exam?.title || "Mock Assessment"}</div>
          <div className="flex items-center gap-4 md:gap-5 text-xs font-bold overflow-x-auto pb-1 md:pb-0 w-full md:w-auto no-scrollbar">
            {currentQuestion?.allow_calculator && (
              <button onClick={() => setIsCalculatorOpen(!isCalculatorOpen)} className="shrink-0 text-sky-300 hover:text-sky-100 font-bold border border-sky-400/50 px-2 rounded bg-sky-900/30">
                {UI_LABELS[language].calculator}
              </button>
            )}
            <button onClick={() => setIsPaused(true)} className="bg-white/10 hover:bg-white/20 px-3 py-1 rounded shrink-0">{UI_LABELS[language].pause}</button>
            <button onClick={() => setActivePopup("group_instructions")} className="shrink-0 hover:text-slate-300">{UI_LABELS[language].groupInstBtn}</button>
            <button onClick={() => setActivePopup("instructions")} className="shrink-0 hover:text-slate-300">{UI_LABELS[language].instBtn}</button>
            <button onClick={() => setActivePopup("question_paper")} className="shrink-0 hover:text-slate-300">{UI_LABELS[language].paperBtn}</button>
          </div>
        </div>

        <div className="flex items-center justify-between gap-3 border-b bg-slate-100 px-3 py-2 text-xs relative z-30">
          <div className="flex items-center gap-2 flex-wrap flex-1 pb-1">
            {groupNames.map((group) => {
              const groupStats = getGroupStatusCounts(group);
              return (
                <div key={group} className="relative group shrink-0">
                  <button
                    key={group}
                    onClick={() => {
                      const firstSection = Object.keys(groupedQuestions[group] || {})[0];
                      jumpToSection(group, firstSection);
                    }}
                    className={`flex items-center gap-1.5 border px-3 py-1.5 rounded-t-sm ${activeGroup === group ? "bg-[#0c73a8] text-white border-[#0c73a8]" : "bg-white text-slate-700 border-slate-300"}`}
                  >
                    {group}
                    <Info size={16} fill="#38bdf8" className="text-white bg-white rounded-full overflow-hidden" />
                  </button>

                  <div className="absolute top-full left-0 mt-0 hidden group-hover:block z-50 w-64 bg-[#eaf4f9] shadow-xl border border-slate-300 text-sm text-slate-800">
                    <div className="font-bold text-[15px] px-3 py-2 border-b border-white bg-[#e3f0f7] text-black">
                      {group}
                    </div>
                    <div className="p-3 space-y-3">
                      <div className="flex items-center gap-3">
                        <span className={`relative inline-flex h-7 w-7 shrink-0 items-center justify-center text-xs font-black ${statusClass('answered')}`}>
                          {groupStats.answered}
                        </span>
                        <span>{UI_LABELS[language].ans}</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className={`relative inline-flex h-7 w-7 shrink-0 items-center justify-center text-xs font-black ${statusClass('notAnswered')}`}>
                          {groupStats.notAnswered}
                        </span>
                        <span>{UI_LABELS[language].notAns}</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className={`relative inline-flex h-7 w-7 shrink-0 items-center justify-center text-xs font-black ${statusClass('notVisited')}`}>
                          {groupStats.notVisited}
                        </span>
                        <span>{UI_LABELS[language].notVis}</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className={`relative inline-flex h-7 w-7 shrink-0 items-center justify-center text-xs font-black ${statusClass('marked')}`}>
                          {groupStats.marked}
                        </span>
                        <span>{UI_LABELS[language].markRev}</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className={`relative inline-flex h-7 w-7 shrink-0 items-center justify-center text-xs font-black ${statusClass('answeredMarked')}`}>
                          {groupStats.answeredMarked}
                          <span className="absolute -bottom-0.5 -right-0.5 h-3.5 w-3.5 rounded-sm bg-[#5cb85c] border border-white" />
                        </span>
                        <span>{UI_LABELS[language].ansMark}</span>
                      </div>
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
          <div className="shrink-0 font-black border-l border-slate-300 pl-3">{UI_LABELS[language].timeLeft} {formattedTime}</div>
        </div>

        {sectionNames.length > 0 && !(sectionNames.length === 1 && !sectionNames[0]) && (
          <div className="flex items-center gap-2 border-b bg-white px-3 py-2 text-xs overflow-x-auto no-scrollbar pb-1">
            <span className="font-bold mr-2 shrink-0">{UI_LABELS[language].sections}</span>
            {sectionNames.map((section) => (
              <button
                key={section}
                onClick={() => {
                  jumpToSection(activeGroup, section);
                }}
                className={`border px-3 py-1.5 font-bold shrink-0 ${activeSection === section ? "bg-sky-600 text-white" : "bg-white text-sky-700"}`}
              >
                {section || "General"}
              </button>
            ))}
          </div>
        )}

        <div className="flex flex-col lg:flex-row flex-1 overflow-hidden relative">
          <main className="relative flex flex-col flex-1 lg:border-r border-slate-300 overflow-hidden w-full">
            <button
              onClick={() => setIsSidebarOpen(!isSidebarOpen)}
              className={`absolute top-1/2 -translate-y-1/2 z-50 bg-white border border-slate-300 shadow p-1.5 rounded-l text-slate-500 hover:text-slate-800 hover:bg-slate-50 transition-all ${isSidebarOpen ? "right-[250px] lg:right-0 lg:border-r-0" : "right-0 border-r-0"}`}
              title={isSidebarOpen ? "Collapse sidebar" : "Expand sidebar"}
            >
              {isSidebarOpen ? <ChevronRight size={20} /> : <ChevronLeft size={20} />}
            </button>

            <div className="flex flex-wrap items-center justify-between gap-2 border-b px-3 py-2 text-xs">
              <span className="font-bold">{UI_LABELS[language].qType} {currentQuestion?.question_type || "Multiple Choice Question"}</span>
              <span>{UI_LABELS[language].marks} <b>{currentQuestion?.marks ?? 1}</b> | {UI_LABELS[language].negMarks} <b className="text-red-600">{currentQuestion?.negative_marks ?? 0.5}</b></span>
            </div>

            <div className="bg-sky-700 px-3 py-2 text-right text-xs font-bold text-white">
              {UI_LABELS[language].viewIn} <select value={language} onChange={(e) => setLanguage(e.target.value)} className="ml-1 text-slate-900 px-1.5 py-0.5 rounded border border-slate-300 font-bold"><option value="English">English</option><option value="Hindi">Hindi</option></select>
            </div>

            <div className="flex-1 flex flex-col md:flex-row bg-white overflow-hidden">
              {currentQuestion?.linked_prompt && (
                <div className="flex-1 border-r border-slate-300 overflow-y-auto p-4">
                  <h3 className="border-b pb-2 text-lg font-black">Linked Answer Question</h3>
                  {isTranslating && language === "Hindi" && !translationCache[currentQuestion.id] && <div className="animate-pulse italic text-xs text-sky-600 mb-2">Translating...</div>}
                  <RichContent html={renderQText("linked_prompt")} className="mt-4 text-base" />
                </div>
              )}
              <div className={`overflow-y-auto p-4 ${currentQuestion?.linked_prompt ? "flex-1" : "w-full"}`}>
                <h3 className="border-b pb-2 text-lg font-black">
                  {currentQuestion?.is_mandatory && <span className="text-red-600">*</span>} {UI_LABELS[language].qNo} {questionIndex + 1}
                </h3>
                {currentQuestion?.is_mandatory && (
                  <div className="my-4 border border-amber-400 bg-yellow-50 px-4 py-3 text-sm font-bold flex items-start gap-2">
                    <AlertTriangle size={18} fill="currentColor" className="text-amber-500 flex-shrink-0 mt-0.5" />
                    <span>{UI_LABELS[language].mandatoryAlert}</span>
                  </div>
                )}
                {currentQuestion?.cannot_edit_response && (
                  <div className="my-4 border border-amber-400 bg-yellow-50 px-4 py-3 text-sm font-bold flex items-start gap-2">
                    <AlertTriangle size={18} fill="currentColor" className="text-amber-500 flex-shrink-0 mt-0.5" />
                    <span>{UI_LABELS[language].notEditAlert}</span>
                  </div>
                )}
                {currentQuestion?.cannot_revisit && (
                  <div className="my-4 border border-amber-400 bg-yellow-50 px-4 py-3 text-sm font-bold flex items-start gap-2">
                    <AlertTriangle size={18} fill="currentColor" className="text-amber-500 flex-shrink-0 mt-0.5" />
                    <span>{UI_LABELS[language].notRevisitAlert}</span>
                  </div>
                )}
                {isTranslating && language === "Hindi" && !translationCache[currentQuestion?.id] && <div className="animate-pulse italic text-xs text-sky-600 mb-2">Translating to Hindi...</div>}
                <RichContent html={renderQText("prompt")} className="mb-5 text-base" />

                {currentQuestion?.question_type === "Subjective Question" ? (
                  <div className="mt-4">
                    <textarea
                      className="w-full h-32 p-3 border border-slate-300 rounded outline-none focus:border-sky-500 focus:ring-1 focus:ring-sky-500 text-sm"
                      placeholder={language === "Hindi" ? "अपना उत्तर यहाँ लिखें..." : "Type your answer here..."}
                      value={answers[currentQuestion.id] || ""}
                      onChange={(e) => setAnswer(currentQuestion.id, e.target.value)}
                      disabled={Boolean(result) || (currentQuestion?.cannot_edit_response && locked[currentQuestion.id])}
                    />
                    <div className="text-xs text-sky-600 font-bold mt-1">{(answers[currentQuestion.id] || "").trim().split(/\s+/).filter(Boolean).length} {language === "Hindi" ? "शब्द लिखे गए" : "words typed"}</div>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {currentQuestion && (
                      currentQuestion.options && currentQuestion.options.length > 0
                        ? (language === "Hindi" && translationCache[currentQuestion.id]?.options
                          ? translationCache[currentQuestion.id].options.map(o => [o.id, o.text])
                          : currentQuestion.options.map(o => [o.id, o.text]))
                        : [
                          ["A", renderQText("option_a")],
                          ["B", renderQText("option_b")],
                          ["C", renderQText("option_c")],
                          ["D", renderQText("option_d")],
                        ].filter(([l, t]) => t)
                    ).map(([option, text]) => {
                      const isSelected = answers[currentQuestion.id] === option;
                      return (
                        <motion.label
                          whileHover={{ scale: 1.01, x: 5 }}
                          whileTap={{ scale: 0.98 }}
                          key={option}
                          className={`flex cursor-pointer items-start gap-4 p-4 rounded-2xl border-2 transition-all text-base ${isSelected ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-900/20 shadow-md' : 'border-slate-200 dark:border-slate-700 hover:border-indigo-300'}`}
                        >
                          <input
                            type="radio"
                            name={`question-${currentQuestion.id}`}
                            className="mt-1 w-5 h-5 text-indigo-600 accent-indigo-600"
                            checked={isSelected}
                            disabled={Boolean(result) || (currentQuestion?.cannot_edit_response && locked[currentQuestion.id])}
                            onChange={() => setAnswer(currentQuestion.id, option)}
                          />
                          <div className="flex gap-2 w-full">
                            <span className={`font-black shrink-0 ${isSelected ? 'text-indigo-600' : 'text-slate-500'}`}>{option}.</span>
                            <RichContent html={text} className={`${isSelected ? 'font-bold' : ''}`} />
                          </div>
                        </motion.label>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>

            <div className="flex flex-wrap items-center justify-between gap-3 border-t bg-slate-50 px-3 py-2">
              <div className="flex gap-3">
                <button onClick={markForReviewAndNext} disabled={Boolean(result)} className="border border-slate-300 bg-white px-5 py-2 text-sm disabled:opacity-50">{UI_LABELS[language].markReview}</button>
                <button onClick={clearAnswer} disabled={Boolean(result)} className="border border-slate-300 bg-white px-5 py-2 text-sm disabled:opacity-50">{UI_LABELS[language].clear}</button>
              </div>
              <div className="flex gap-3">
                <button onClick={handlePrevious} className="border border-slate-300 bg-white px-5 py-2 text-sm">{UI_LABELS[language].prev}</button>
                <button onClick={saveAndNext} className="bg-sky-700 px-5 py-2 text-sm font-bold text-white">{UI_LABELS[language].saveNext}</button>
                <button onClick={handleSubmit} disabled={Boolean(result)} className="bg-sky-600 px-7 py-2 text-sm font-bold text-white disabled:bg-slate-300">{UI_LABELS[language].submit}</button>
              </div>
            </div>
          </main>

          {isSidebarOpen && (
            <>
              {/* Mobile overlay */}
              <div className="lg:hidden absolute inset-0 bg-slate-900/20 z-30" onClick={() => setIsSidebarOpen(false)} />
              <aside className="absolute right-0 top-0 bottom-0 z-40 w-[250px] lg:relative flex-shrink-0 flex flex-col bg-sky-50 overflow-hidden shadow-2xl lg:shadow-none border-l border-slate-300">
                <div className="flex-shrink-0 flex items-center gap-3 border-b bg-slate-100 p-3">
                  <div className="h-16 w-16 bg-slate-300" />
                  <div className="font-black text-slate-900">{UI_LABELS[language].student}</div>
                </div>
                <div className="space-y-2 border-b p-3 text-xs">
                  {[
                    ["notVisited", UI_LABELS[language].notVis],
                    ["notAnswered", UI_LABELS[language].notAns],
                    ["answered", UI_LABELS[language].ans],
                    ["marked", UI_LABELS[language].markRev],
                    ["answeredMarked", UI_LABELS[language].ansMark],
                  ].map(([status, label], index) => (
                    <div key={`${status}-${index}`} className="flex items-center gap-2">
                      <span className={`relative inline-flex h-7 w-7 shrink-0 items-center justify-center text-xs font-black ${statusClass(status)}`}>
                        {statusCounts[status]}
                        {status === "answeredMarked" && <span className="absolute -bottom-0.5 -right-0.5 h-3.5 w-3.5 rounded-sm bg-[#5cb85c] border border-white" />}
                      </span>
                      <span>{label}</span>
                    </div>
                  ))}
                </div>
                <div className="flex-shrink-0 bg-sky-700 px-3 py-2 text-lg font-black text-white">{activeSection}</div>
                <div className="flex-1 overflow-y-auto p-3">
                  <div className="mb-3 text-xs font-bold">{UI_LABELS[language].chooseQ}</div>
                  <div className="grid grid-cols-4 gap-2">
                    {visibleQuestions.map((question, index) => {
                      const status = getStatus(question);
                      return (
                        <div key={question.id} className="relative">
                          <button
                            onClick={() => setQuestion(index)}
                            className={`relative w-full h-11 text-sm font-bold ${statusClass(status)} ${index === questionIndex ? "ring-2 ring-sky-700 ring-offset-2" : ""}`}
                          >
                            {index + 1}
                            {status === "answeredMarked" && <span className="absolute -bottom-0.5 -right-0.5 h-3.5 w-3.5 rounded-sm bg-[#5cb85c] border border-white z-10" />}
                          </button>
                          {question.is_mandatory && <span className="absolute -top-1.5 -right-1.5 h-4 w-4 rounded-full bg-red-600 border border-white text-white flex items-center justify-center text-[10px] font-black shadow-sm leading-none z-20">*</span>}
                        </div>
                      );
                    })}
                  </div>
                </div>
              </aside>
            </>
          )}
        </div>
      </div>

      {activePopup && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-slate-900/80 backdrop-blur-sm">
          <div className="bg-white max-h-[90vh] w-full max-w-4xl flex flex-col rounded shadow-2xl">
            <div className="flex justify-between items-center bg-neutral-800 px-4 py-3 text-white font-bold">
              <span>
                {activePopup === "instructions" && UI_LABELS[language].generalInst}
                {activePopup === "group_instructions" && UI_LABELS[language].groupInstBtn}
                {activePopup === "question_paper" && UI_LABELS[language].paperBtn}
              </span>
              <button onClick={() => setActivePopup(null)} className="text-white hover:text-red-400">{language === "Hindi" ? "बंद करें" : "Close"}</button>
            </div>
            <div className="flex-1 overflow-auto p-6 text-sm leading-6">
              {activePopup === "instructions" && (
                <section>
                  <ol className="list-decimal pl-6 space-y-2">
                    {UI_LABELS[language].instItems.map((item, idx) => (
                      <li key={idx}>{item}</li>
                    ))}
                  </ol>
                </section>
              )}
              {activePopup === "group_instructions" && (
                <>
                  <section className="mb-4">
                    <p className="font-bold mb-2">{UI_LABELS[language].groupsIntro(groupNames.length)}</p>
                    <ol className="list-decimal pl-6">
                      {groupNames.map((group) => <li key={group}>{group}</li>)}
                    </ol>
                  </section>
                  <section className="space-y-3">
                    {groupNames.map((group, i) => {
                      const sectionsInGroup = Object.keys(groupedQuestions[group] || {});
                      const sectionCount = sectionsInGroup.length;
                      const sectionListText = sectionsInGroup.join(", ");
                      return (
                        <p key={group}>
                          {UI_LABELS[language].groupItem(i, group, sectionCount, sectionListText)}
                        </p>
                      );
                    })}
                  </section>
                </>
              )}
              {activePopup === "question_paper" && (
                <div className="space-y-6">
                  {exam?.questions?.map((q, idx) => {
                    const qTrans = language === "Hindi" ? translationCache[q.id] : null;
                    return (
                      <div key={q.id} className="border-b pb-4">
                        <h4 className="font-black mb-2 flex items-center justify-between">
                          <span>{UI_LABELS[language].qNo} {idx + 1}</span>
                          <span className="text-xs font-bold text-sky-600">{q.group_name} / {q.section_name}</span>
                        </h4>
                        <RichContent html={qTrans?.prompt || q.prompt} />
                        <div className="mt-3 space-y-2 text-sm text-slate-600">
                          <div className="flex gap-2"><b>A.</b> <RichContent html={qTrans?.option_a || q.option_a} className="inline" /></div>
                          <div className="flex gap-2"><b>B.</b> <RichContent html={qTrans?.option_b || q.option_b} className="inline" /></div>
                          <div className="flex gap-2"><b>C.</b> <RichContent html={qTrans?.option_c || q.option_c} className="inline" /></div>
                          <div className="flex gap-2"><b>D.</b> <RichContent html={qTrans?.option_d || q.option_d} className="inline" /></div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {isCalculatorOpen && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-transparent pointer-events-none">
          <div className="bg-white border border-slate-300 shadow-2xl rounded-xl w-64 pointer-events-auto flex flex-col overflow-hidden absolute top-20 right-4 md:right-20">
            <div className="bg-slate-800 text-white px-3 py-2 text-sm font-bold flex justify-between items-center">
              {UI_LABELS[language].calculator}
              <button onClick={() => setIsCalculatorOpen(false)} className="hover:text-red-400">✕</button>
            </div>
            <div className="p-3 bg-slate-100">
              <input
                type="text"
                value={calcInput}
                readOnly
                className="w-full text-right p-2 text-lg font-mono border border-slate-300 rounded mb-3 bg-white text-slate-900"
              />
              <div className="grid grid-cols-4 gap-2 text-sm font-bold text-slate-800">
                {['C', '(', ')', '/'].map(btn => (
                  <button key={btn} onClick={() => {
                    if (btn === 'C') setCalcInput("");
                    else setCalcInput(prev => prev === "Error" ? btn : prev + btn);
                  }} className="bg-slate-200 hover:bg-slate-300 p-2 rounded">{btn}</button>
                ))}
                {['7', '8', '9', '*'].map(btn => (
                  <button key={btn} onClick={() => setCalcInput(prev => prev === "Error" ? btn : prev + btn)} className="bg-slate-200 hover:bg-slate-300 p-2 rounded">{btn}</button>
                ))}
                {['4', '5', '6', '-'].map(btn => (
                  <button key={btn} onClick={() => setCalcInput(prev => prev === "Error" ? btn : prev + btn)} className="bg-slate-200 hover:bg-slate-300 p-2 rounded">{btn}</button>
                ))}
                {['1', '2', '3', '+'].map(btn => (
                  <button key={btn} onClick={() => setCalcInput(prev => prev === "Error" ? btn : prev + btn)} className="bg-slate-200 hover:bg-slate-300 p-2 rounded">{btn}</button>
                ))}
                <button onClick={() => setCalcInput(prev => prev === "Error" ? "0" : prev + '0')} className="bg-slate-200 hover:bg-slate-300 p-2 rounded col-span-2">0</button>
                <button onClick={() => setCalcInput(prev => prev === "Error" ? "." : prev + '.')} className="bg-slate-200 hover:bg-slate-300 p-2 rounded">.</button>
                <button onClick={() => {
                  try {
                    // eslint-disable-next-line
                    setCalcInput(String(eval(calcInput)));
                  } catch (e) {
                    setCalcInput("Error");
                  }
                }} className="bg-sky-600 text-white hover:bg-sky-700 p-2 rounded">=</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
